import { marked } from 'marked';
import DOMPurify from 'dompurify';
import React, { useEffect, useRef, useState } from 'react';

import { MermaidAddon } from '../../pages/Adr/Mermaid';
import { getBase64ImageByUrl } from '../../services/image.service';

const yaml = require('js-yaml');

const yamlFrontmatterRegex = /^---\s*\r?\n([\s\S]*?)\r?\n---\s*\r?\n/m;
const mermaidRegex = /```[\s]*mermaid([\s\S]*?)```/g;

export const sanitizeMarkdownHtml = (html: string) =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'a', 'blockquote', 'br', 'caption', 'code', 'dd', 'del', 'div', 'dl',
      'dt', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'img', 'li',
      'ol', 'p', 'pre', 's', 'strong', 'sub', 'sup', 'table', 'tbody', 'td',
      'th', 'thead', 'tr', 'ul',
    ],
    ALLOWED_ATTR: [
      'alt', 'colspan', 'href', 'height', 'rel', 'rowspan', 'src', 'target',
      'title', 'width',
    ],
  });

export const RenderMarkDown = ({ retrievedContents, url, innerUrl }: any) => {
  let htmlText = retrievedContents;
  // Handle links
  htmlText = htmlText?.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2">$1</a>',
  );

  // Handle bold and italic
  htmlText = htmlText?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  htmlText = htmlText?.replace(/__(.*?)__/g, '<strong>$1</strong>');
  htmlText = htmlText?.replace(/\*(.*?)\*/g, '<em>$1</em>');
  htmlText = htmlText?.replace(/_(.*?)_/g, '<em>$1</em>');

  // Handle inline code
  htmlText = htmlText?.replace(/`(.*?)`/g, '<code>$1</code>');

  // Handle paragraphs
  htmlText = htmlText?.replace(/^\s*(.*?)\s*$/gm, '<p>$1</p>');

  htmlText = htmlText?.replace(
    /<([^>\s]+)>/g,
    (_: any, url_: string) => `<a href="${url_}">${url_}</a>`,
  );

  return (
    <RenderMdWithInLineImage
      part={retrievedContents}
      url={url}
      innerUrl={innerUrl}
    />
  );
};

function renderValue(value: any) {
  try {
    if (Array.isArray(value)) {
      if (value.length > 1) {
        // Check if any item contains a comma
        const isComma = value.some(
          item => typeof item === 'string' && item.includes(','),
        );

        if (isComma) {
          value = value.map(item =>
            typeof item === 'string' ? item.replace(/,/g, '') : item,
          );
        }

        return value.join(', ');
      } else {
        const arr = value[0];

        if (typeof arr === 'string') {
          // Handle <https://example.com> format
          if (arr.startsWith('<https://') && arr.endsWith('>')) {
            const url = arr.slice(1, -1);

            return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
          }

          // Handle Markdown link format [text](https://url.com)
          const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g;
          const match = markdownLinkRegex.exec(arr);

          if (match) {
            const text = match[1];
            const url = match[2];

            return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
          }
        }

        return arr;
      }
    } else if (value instanceof Date) {
      return value.toISOString().split('T')[0];
    }

    return value;
  } catch (error) {
    console.error('Error rendering value:', error);
    return value;
  }
}

function RenderTable({ data }: any) {
  return (
    <table>
      <tbody>
        {Object.entries(data).map(([key, value]) => (
          <tr key={key}>
            <th>{key}</th>
            <td
              dangerouslySetInnerHTML={{
                __html: sanitizeMarkdownHtml(String(renderValue(value) ?? '')),
              }}
            />
          </tr>
        ))}
      </tbody>
    </table>
  );
}

async function getImage(imgName: string, url: string, innerUrl: string) {
  try {
     url = url?.replace('url:', '');
  if (url?.endsWith('.md')) {
    url = url?.replace('.md', '');
  }
  if (imgName.startsWith('./') || imgName?.includes('%')) {
    // point to the root folder of the adr
    url = url?.substring(0, url.lastIndexOf('/')).concat('/');
  }
  const response = await getBase64ImageByUrl(
    innerUrl
      ? url
          ?.concat('/')
          .concat(innerUrl)
          ?.concat('/')
          .concat(imgName?.replace('./', ''))
          ?.replace('%2F', '/')
      : url
          ?.concat('/')
          .concat(imgName?.replace('./', ''))
          ?.replace('%2F', '/'),
  );

  return response;
  } catch (error) {
    console.error('Error fetching image:', error);
  }
  return null;
}

async function getBase64ForAttachment(url: string) {
  try {
    const response = await getBase64ImageByUrl(
      url?.concat('/').concat(url?.replace('./', ''))?.replace('%2F', '/'),
    );
    return response;
  } catch (error) {
    console.error('Error fetching attachment:', error);
    return null;
  }
}

function RenderMdWithInLineImage({ part, url, innerUrl }: any) {
  const markdownRef = useRef(part);
  const yamlDataRef = useRef<any>(null);
  const diagramsRef = useRef<string[]>([]);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchImagesAndConvertToBase64 = async () => {
      setIsReady(false);
      yamlDataRef.current = null;

      const imgTagRegex = /!\[.*?\]\((.*?\.svg|.*?\.png|.*?\.jpg)\)/g;
      let match;
      const imagePromises = [];
      const images = [];

      while ((match = imgTagRegex.exec(part)) !== null) {
        const altText = match[1];
        const imageUrl = match[2];
        images.push({ original: match[0], altText, imageUrl });
        imagePromises.push(getImage(altText, url, innerUrl));
      }

      const base64Images = await Promise.all(imagePromises);
      let updatedContent = part;

      // Inline base64 images
      if (base64Images.length > 0) {
        for (let i = 0; i < base64Images.length; i++) {
          const base64Image = base64Images[i];
          if (!base64Image) continue;
          const { original } = images[i];

          let imgSrc = `data:image/png;base64,${base64Image}`;
          try {
            if (atob(base64Image).includes('<svg')) {
              imgSrc = `data:image/svg+xml;base64,${base64Image}`;
            }
          } catch {}
          updatedContent = updatedContent?.replace(
            original,
            `![${images[i].altText}](${imgSrc})`,
          );
        }
      }

      // YAML frontmatter
      if (yamlFrontmatterRegex.test(updatedContent)) {
        const splitYamlformatter = updatedContent
          .split(yamlFrontmatterRegex)
          .slice(1);
        const yamlContent = splitYamlformatter[0];
        try {
          yamlDataRef.current = yaml.load(yamlContent);
        } catch {}
        updatedContent = updatedContent?.replace(yamlFrontmatterRegex, '');
      }

      // Mermaid blocks
      const mermaidMatches = Array.from(
        updatedContent.matchAll(mermaidRegex),
      ) as any;
      diagramsRef.current = mermaidMatches.map((m: any) => m[1].trim());
      diagramsRef.current.forEach((diagram: string, index: number) => {
        updatedContent = updatedContent?.replace(
          mermaidMatches[index][0],
          'mer_maid'.concat(diagram).concat('mer_maid'),
        );
      });

      const attachmentRegex = /\[.*?\]\(([^)]+\.pdf)\)/g;

      const attachmentMatches = Array.from(part.matchAll(attachmentRegex));

      const data = attachmentMatches.map((m: any) => m[1].trim());

      console.log(data);

      const getBase64 = await Promise.all(
        data.map((attachment: string) => {
          console.log('Processing attachment:', attachment);
          console.log('Processing attachment url:', url);

          return getBase64ForAttachment(
            url.concat(
              '/' + attachment?.replace('%2F', '/').replace('./', '/'),
            ),
          );
        }),
      );

      let updatedMarkdown = updatedContent;

      attachmentMatches.forEach((match: any, index: number) => {
        const base64 = getBase64[index];

        if (!base64) return;

        // Handle both raw base64 and data URLs
        const rawBase64 = base64.replace(/^data:application\/pdf;base64,/, '');

        const byteCharacters = atob(rawBase64);
        const byteNumbers = Array.from(byteCharacters, c => c.charCodeAt(0));

        const byteArray = new Uint8Array(byteNumbers);

        const blob = new Blob([byteArray], {
          type: 'application/pdf',
        });

        const blobUrl = URL.createObjectURL(blob);

        const originalMarkdown = match[0];
        const linkText = originalMarkdown.match(/\[(.*?)\]/)?.[1] ?? 'PDF';

        const replacement = `<a href="${blobUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;

        updatedMarkdown = updatedMarkdown.replace(
          originalMarkdown,
          replacement,
        );
      });

      markdownRef.current = updatedMarkdown;

      //markdownRef.current = updatedContent;

      if (!cancelled) setIsReady(true); // trigger one render
    };

    fetchImagesAndConvertToBase64();

    return () => {
      cancelled = true; // prevent update on unmount
    };
  }, [part, url]);

  if (!isReady) {
    return <div>Loading content...</div>; // optional loading state
  }

  return (
    <>
      {yamlDataRef.current && <RenderTable data={yamlDataRef.current} />}
      {markdownRef.current
        ?.split('mer_maid')
        .map((item: string, index: number) => {
          if (diagramsRef.current.includes(item)) {
            return <MermaidAddon key={index} retrievedContent={item} />;
          }
          return (
            <div
              style={{ overflow: 'scroll' }}
              key={index}
              dangerouslySetInnerHTML={{
                __html: sanitizeMarkdownHtml(marked.parse(item).toString()),
              }}
            />
          );
        })}
    </>
  );
}
